"use strict";
/*
 * IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: IBM Cognos Analytics
 *
 * (C) Copyright IBM Corp. 2020, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 */
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const react_1 = tslib_1.__importStar(require("react"));
const carbon_components_react_1 = require("carbon-components-react");
const utils_1 = require("../utils");
const COMPONENT_NAME = 'DatePicker';
const DISPLAY_NAME = (0, utils_1.getDisplayName)(COMPONENT_NAME);
const DEFAULT_LOCALE = 'en';
const DEFAULT_TID = (0, utils_1.getDefaultTid)(COMPONENT_NAME);
// Carbon locales come from https://github.com/flatpickr/flatpickr/tree/master/src/l10n
// Even some of the locales supported by flatpickr are not supported in the Carbon version we have.
const carbonLocales = Object.freeze([
    'ar',
    'at',
    // 'az', // not supported in our version of Carbon
    'be',
    'bg',
    'bn',
    // 'bs', // not supported in our version of Carbon
    'cat',
    'cs',
    'cy',
    'da',
    'de',
    'en',
    'eo',
    'es',
    'et',
    'fa',
    'fi',
    // 'fo', // not supported in our version of Carbon
    'fr',
    // 'ga', // not supported in our version of Carbon
    'gr',
    'he',
    'hi',
    'hr',
    'hu',
    'id',
    // 'is', // not supported in our version of Carbon
    'it',
    'ja',
    // 'ka', // not supported in our version of Carbon
    // 'km', // not supported in our version of Carbon
    'ko',
    // 'kz', // not supported in our version of Carbon
    'lt',
    'lv',
    'mk',
    'mn',
    'ms',
    'my',
    'nl',
    'no',
    'pa',
    'pl',
    'pt',
    'ro',
    // 'ru', // not supported in our version of Carbon
    'si',
    'sk',
    'sl',
    'sq',
    // 'sr-cyr', // not supported in our version of Carbon
    'sr',
    'sv',
    'th',
    'tr',
    'uk',
    // 'uz', // not supported in our version of Carbon
    // 'uz_latn', // not supported in our version of Carbon
    'vn',
    // 'zh-tw', // not supported in our version of Carbon
    'zh',
]);
class ForwardRefDatePicker extends react_1.Component {
    constructor() {
        super(...arguments);
        // mapLocale is a fix for...
        // https://github.com/carbon-design-system/carbon/issues/7709
        this.mapLocale = locale => {
            let tempLocale = locale || DEFAULT_LOCALE;
            while (tempLocale) {
                if (carbonLocales.includes(tempLocale)) {
                    break;
                }
                const suffixLocale = tempLocale.lastIndexOf('-');
                if (suffixLocale === -1) {
                    // no suffix locale to remove and no base locale matched
                    // default to English 'en'
                    tempLocale = DEFAULT_LOCALE;
                    break;
                }
                tempLocale = tempLocale.slice(0, suffixLocale);
            }
            if (tempLocale !== locale) {
                console.warn(`Carbon does not support the locale ${locale !== null && locale !== void 0 ? locale : 'undefined'}`);
                console.warn(`Using the locale ${tempLocale}`);
            }
            return tempLocale;
        };
    }
    render() {
        const _a = this.props, { 
        // remove - remapped and local props
        appendTo, 'data-tid': dataTid, innerRef, locale } = _a, 
        // pass all other props to Carbon
        otherCarbonProps = tslib_1.__rest(_a, ["appendTo", 'data-tid', "innerRef", "locale"]);
        const mappedProps = {
            'data-tid': dataTid !== null && dataTid !== void 0 ? dataTid : DEFAULT_TID,
            locale: this.mapLocale(locale),
        };
        if (appendTo) {
            mappedProps.appendTo = appendTo;
        }
        if (innerRef) {
            mappedProps.ref = innerRef;
        }
        return react_1.default.createElement(carbon_components_react_1.DatePicker, Object.assign({}, otherCarbonProps, mappedProps));
    }
}
const DatePicker = react_1.default.forwardRef((props, ref) => {
    const otherProps = ref ? { innerRef: ref } : {};
    return react_1.default.createElement(ForwardRefDatePicker, Object.assign({}, props, otherProps));
});
DatePicker.displayName = DISPLAY_NAME;
exports.default = DatePicker;
